import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { TagCriteriaBuilder } from "./TagCriteriaBuilder";
import { IconSelector } from "./IconSelector";
import * as tagsApi from "@/services/tagsApi";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  type: z.enum(["static", "dynamic"]),
  color: z.string().min(1, "Color is required"),
  icon: z.string().optional(),
});

import { MappedTag } from "@/utils/tagMapping";

interface EditTag {
  id: string; name: string; description: string | null; type: string; color: string; icon?: string; criteria: any;
}

interface EditTagDialogProps {
  open: boolean; onOpenChange: (open: boolean) => void; tag: EditTag; onTagUpdated: () => void;
}

export function EditTagDialog({ open, onOpenChange, tag, onTagUpdated }: EditTagDialogProps) {
  const [criteria, setCriteria] = useState(tag.criteria);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: tag.name, description: tag.description || "", type: tag.type as "static" | "dynamic", color: tag.color, icon: tag.icon || "Tag" },
  });

  useEffect(() => {
    form.reset({ name: tag.name, description: tag.description || "", type: tag.type as "static" | "dynamic", color: tag.color, icon: tag.icon || "Tag" });
    setCriteria(tag.criteria);
  }, [tag, form]);

  const watchedType = form.watch("type");

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      if (values.type === "dynamic" && !criteria) {
        form.setError("type", { type: "manual", message: "Dynamic tags require criteria to be defined" });
        return;
      }

      await tagsApi.updateTagWithInput(tag.id, {
        name: values.name,
        description: values.description || "",
        type: values.type,
        color: values.color,
        icon: values.icon || null,
        criteria: values.type === "dynamic" ? criteria : null,
      });

      toast({ title: "Tag updated", description: "Tag has been updated successfully." });
      onTagUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating tag:", error);
      toast({ title: "Error", description: "Failed to update tag. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Tag</DialogTitle>
          <DialogDescription>Update the tag information and criteria.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="name" render={({ field }) => (<FormItem><FormLabel>Name</FormLabel><FormControl><Input placeholder="Enter tag name" {...field} /></FormControl><FormMessage /></FormItem>)} />
              <FormField control={form.control} name="type" render={({ field }) => (<FormItem><FormLabel>Type</FormLabel><Select onValueChange={field.onChange} value={field.value}><FormControl><SelectTrigger><SelectValue placeholder="Select tag type" /></SelectTrigger></FormControl><SelectContent><SelectItem value="static">Static</SelectItem><SelectItem value="dynamic">Dynamic</SelectItem></SelectContent></Select><FormMessage /></FormItem>)} />
            </div>
            <FormField control={form.control} name="description" render={({ field }) => (<FormItem><FormLabel>Description</FormLabel><FormControl><Textarea placeholder="Enter tag description" className="resize-none" rows={3} {...field} /></FormControl><FormMessage /></FormItem>)} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="color" render={({ field }) => (<FormItem><FormLabel>Color</FormLabel><FormControl><div className="flex items-center space-x-2"><Input type="color" className="w-12 h-10 p-1 border rounded" {...field} /><Input placeholder="#3B82F6" className="flex-1" {...field} /></div></FormControl><FormMessage /></FormItem>)} />
              <FormField control={form.control} name="icon" render={({ field }) => (<FormItem><FormLabel>Icon</FormLabel><FormControl><IconSelector value={field.value} onChange={field.onChange} placeholder="Select an icon" /></FormControl><FormMessage /></FormItem>)} />
            </div>
            {watchedType === "dynamic" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between"><h3 className="text-lg font-medium">Tag Criteria</h3><Badge variant="default">Dynamic</Badge></div>
                <p className="text-sm text-muted-foreground">Define conditions that devices must meet to automatically receive this tag.</p>
                <TagCriteriaBuilder value={criteria} onChange={setCriteria} />
              </div>
            )}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>{isLoading ? "Updating..." : "Update Tag"}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
